import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPerson } from '@common/utils/getDisplayName';
import { SortDirection } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage } from '../../../translate';
import { ConfirmationModal } from '../../elements/modals/ConfirmationModal';
import { NullableStringBirthDate } from '../../elements/NullableStringBirthDate';

export interface IFundusNoShow {
    birthDate: string;
    careGroup: string;
    fullName: string;
    gender: string;
    generalPractitioner: IPerson;
    id: string;
    identifierSocialSecurity: string;
    isRemoved: boolean;
    organizationId: string;
    period: string;
    updated_at: string;
}

interface FundusNoShowProps {
    fundusNoShows: IFundusNoShow[];
    isAdmin: boolean;
    onRemoveNoShow: (fundusNoShow: IFundusNoShow) => Promise<void>;
}

export function FundusNoShowsGroup(props: Readonly<FundusNoShowProps>) {
    const { isAdmin, fundusNoShows, onRemoveNoShow } = props;

    const [showModal, setShowModal] = useState(false);
    const [selectedFundusNoShow, setSelectedFundusNoShow] = useState<IFundusNoShow>();
    const [careGroupNameSortOrder, setCareGroupNameSortOrder] = useState<SortDirection>(SortDirection.ascending);
    const [orderedFundusNoShows, setOrderedFundusNoShows] = useState<IFundusNoShow[]>(fundusNoShows);

    const translate = getSelectedLanguage();
    const smallTableCellWidth = isAdmin ? '8%' : '14%';
    const bigTableCellWidth = isAdmin ? '24%' : '25%';

    useEffect(() => {
        setOrderedFundusNoShows(
            [...fundusNoShows].sort((a, b) =>
                careGroupNameSortOrder === SortDirection.ascending
                    ? a.careGroup.localeCompare(b.careGroup)
                    : b.careGroup.localeCompare(a.careGroup)
            )
        );
    }, [careGroupNameSortOrder, fundusNoShows]);

    return (
        <>
            {!isAdmin && (
                <ConfirmationModal
                    handleNo={() => setShowModal(false)}
                    handleYes={handleRemove}
                    isOpen={showModal}
                    question={`Weet je zeker dat je patiënt ${selectedFundusNoShow?.fullName} uit de no-show lijst wilt verwijderen?`}
                    title={translate.common.notice}
                />
            )}

            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translate.common.name}</TableHeader>
                            <TableHeader>{translate.common.gender}</TableHeader>
                            <TableHeader>{translate.common.birthDate}</TableHeader>
                            <TableHeader>{translate.common.bsn}</TableHeader>
                            {isAdmin && (
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={true}
                                    onClick={handleCareGroupSortOrderChange}
                                    sortDirection={careGroupNameSortOrder}
                                >
                                    {translate.common.careGroup}
                                </TableHeader>
                            )}
                            <TableHeader>{translate.patient.careProvider}</TableHeader>
                            {!isAdmin && <TableHeader></TableHeader>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orderedFundusNoShows.map((fundusNoShow) => (
                            <TableRow
                                data-dd-action-name="fundusNoShows-list-patient"
                                key={fundusNoShow.id}
                            >
                                <TableCell width={bigTableCellWidth}>{fundusNoShow.fullName}</TableCell>
                                <TableCell width={smallTableCellWidth}>
                                    {translate.genderEnum[fundusNoShow.gender as keyof typeof translate.genderEnum]}
                                </TableCell>
                                <TableCell width={smallTableCellWidth}>
                                    <NullableStringBirthDate value={fundusNoShow.birthDate} />
                                </TableCell>
                                <TableCell width={smallTableCellWidth}>
                                    {fundusNoShow.identifierSocialSecurity}
                                </TableCell>
                                {isAdmin && <TableCell width={bigTableCellWidth}>{fundusNoShow.careGroup}</TableCell>}
                                <TableCell width={bigTableCellWidth}>
                                    {getDisplayName(fundusNoShow.generalPractitioner)}
                                </TableCell>
                                {!isAdmin && (
                                    <TableCell width={'6%'}>
                                        <IconButton
                                            icon="delete"
                                            onClick={() => {
                                                setSelectedFundusNoShow(fundusNoShow);
                                                setShowModal(true);
                                            }}
                                        />
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );

    async function handleRemove() {
        setShowModal(false);
        if (selectedFundusNoShow) {
            await onRemoveNoShow(selectedFundusNoShow);
        }
    }

    function handleCareGroupSortOrderChange() {
        setCareGroupNameSortOrder(
            careGroupNameSortOrder === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending
        );
    }
}
